import type * as React from "react";
import { MonthYearPicker } from "@/ui/atoms";
import type { TAsyncState } from "@/store";
import { formatRelativeTime } from "@/utils/relativeTime";
import { BUSINESS_TIMEZONE_LABEL } from "@/modules/rev_apac/clock";
import { monthBounds, type TSubmissionContext } from "./submissionContext";
import type { TFileReviewDetails } from "./types";

const TODAY_FORMATTER = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
});

export type TFileReviewHeaderProps = {
    /** `null` for a restricted caller, who never loads a file. */
    file: TFileReviewDetails | null;
    context: TAsyncState<TSubmissionContext>;
    /** The month being viewed, `YYYY-MM` — the URL's `?period=` when set, else the file's own. */
    selectedPeriod: string;
    /** A month picked from the calendar, `YYYY-MM`. */
    onSelectPeriod: (period: string) => void;
};

export function FileReviewHeader(props: TFileReviewHeaderProps): React.ReactElement {
    const { file, context, selectedPeriod, onSelectPeriod } = props;

    // A failed context must not shout over a working review page — it renders
    // exactly like the no-deal / unresolved case, ignoring any stale data.
    const data = context.status === "rejected" ? null : context.data;
    const isPendingNoData = context.status === "pending" && context.data === null;

    const hasDeal = data !== null && data.dealId !== null;

    // The picker offers the deal's own months — its floor through the current one —
    // because the context endpoint rejects anything else outright.
    const bounds = monthBounds(data);

    /*
     * Built from whatever identity is actually known, most-identifying first.
     *
     * This used to be all-or-nothing — both the business partner and the submission
     * name from the context, or nothing but the filename. That dropped the deal off
     * the page entirely on three real paths: a context that failed, a first load still
     * in flight, and a deal with no service provider. The Review tab no longer repeats
     * the deal name, so the heading is the only place it appears and it has to hold
     * up. `file.dealName` comes from the file payload rather than the context, which
     * is what makes it available on exactly those paths.
     */
    const contextTitle = hasDeal
        ? [data.businessPartnerName, data.submissionName]
              .filter((part) => part !== null)
              .join(" | ")
        : "";
    const title =
        contextTitle !== ""
            ? contextTitle
            : (file?.dealName ?? file?.originalFilename ?? "Supporting files");

    const submittedAgo =
        file !== null && file.uploadedAt !== null ? formatRelativeTime(file.uploadedAt) : null;

    return (
        // Title and submission identity on the left, the period being viewed and
        // today's date on the right — one row, so the page reaches its content
        // without a stack of separate header lines.
        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
            <div className="min-w-0">
                <h1 className="truncate text-2xl font-semibold">{title}</h1>
                {/* No file to have been submitted at all in restricted mode — this line
                    describes the file, not the submission, so it is skipped rather than
                    shown as "Not yet submitted". */}
                {file !== null && (
                    <p
                        data-testid="file-submitted-ago"
                        title={submittedAgo?.full}
                        className="mt-0.5 text-sm text-muted-foreground"
                    >
                        {submittedAgo !== null
                            ? `Submitted ${submittedAgo.label}`
                            : "Not yet submitted"}
                    </p>
                )}
            </div>

            {isPendingNoData ? (
                <div
                    data-testid="file-review-header-skeleton"
                    className="flex animate-pulse items-center gap-3"
                >
                    <div className="h-9 w-40 rounded-lg bg-muted" />
                    <div className="h-5 w-28 rounded bg-muted" />
                </div>
            ) : (
                <div className="flex items-center gap-4">
                    {data !== null && hasDeal && (
                        <div data-testid="file-review-month-picker" className="w-44">
                            {/* The picker's own trigger shows the period, so the label is
                                for assistive tech only — the visual row stays compact. */}
                            <span id="file-review-period-label" className="sr-only">
                                Period
                            </span>
                            <MonthYearPicker
                                value={selectedPeriod}
                                onChange={onSelectPeriod}
                                ariaLabelledBy="file-review-period-label"
                                minMonth={bounds?.min}
                                maxMonth={bounds?.max}
                            />
                        </div>
                    )}

                    {data !== null && (
                        <span
                            data-testid="file-review-today"
                            className="whitespace-nowrap text-sm font-semibold"
                        >
                            {TODAY_FORMATTER.format(new Date(data.today))}
                            <span className="font-normal text-muted-foreground">
                                {" "}
                                · {BUSINESS_TIMEZONE_LABEL}
                            </span>
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}
